import { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Home } from 'lucide-react';
import { StatusBadge } from '@/components/ui/status-badge';
import { formatCurrency, formatAddress, getPropertyTypeLabel, formatArea } from '@/lib/utils';
import type { Property } from '@/types';

export const PropertyCard = memo(function PropertyCard({ property }: { property: Property }) {
  const primaryImage = useMemo(
    () => property.images?.find((img) => img.is_primary) || property.images?.[0],
    [property.images]
  );

  return (
    <Link to={`/properties/${property.id}`} className="card card-interactive group overflow-hidden">
      <div className="aspect-[16/10] bg-background-elevated relative overflow-hidden rounded-md -mx-card -mt-card mb-4">
        {primaryImage?.url ? (
          <img src={primaryImage.url} alt={property.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Home className="w-12 h-12 text-foreground-subtle/30" />
          </div>
        )}
        <div className="absolute top-3 left-3">
          <StatusBadge status={property.status} />
        </div>
      </div>
      <div>
        <h3 className="text-heading truncate">{property.name}</h3>
        <p className="text-small text-foreground-muted truncate mt-1">
          {formatAddress(property)}
        </p>
        
        <div className="flex gap-4 text-small text-foreground-muted mt-2">
          <span>{getPropertyTypeLabel(property.property_type)}</span>
          {property.living_area_sqm && <span>{formatArea(property.living_area_sqm)}</span>}
          {property.year_built && <span>Bj. {property.year_built}</span>}
        </div>

        <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-4">
          <div>
            <p className="text-label">Kaufpreis</p>
            <p className="font-medium">{formatCurrency(property.purchase_price)}</p>
          </div>
          <div>
            <p className="text-label">Kaltmiete</p>
            <p className="font-medium">{formatCurrency(property.rent_cold)}{property.rent_cold && <span className="text-foreground-muted font-normal">/M</span>}</p>
          </div>
        </div>
      </div>
    </Link>
  );
});